import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logout } from '../actions/authActions';

const InactivityTimer = ({ timeout = 1000 }) => {
    const [countdown, setCountdown] = useState(timeout);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const handleLogout = useCallback(() => {
        // Preserve hospital/site selection and HIMS machine token
        const siteCode = localStorage.getItem('site_code');
        const siteId = localStorage.getItem('site_id');
        const hospitalName = localStorage.getItem('hospital_name');
        const token = localStorage.getItem('token');
        const departmentId = localStorage.getItem('departmentId');
        
        // Clear all data
        dispatch(logout());
        localStorage.clear();
        sessionStorage.clear();
        
        // Restore preserved data
        if (siteCode) localStorage.setItem('site_code', siteCode);
        if (siteId) localStorage.setItem('site_id', siteId);
        if (hospitalName) localStorage.setItem('hospital_name', hospitalName);
        if (token) localStorage.setItem('token', token);
        if (departmentId) localStorage.setItem('departmentId', departmentId);
        
        // Clear all cookies
        document.cookie.split(";").forEach((c) => {
            document.cookie = c
                .replace(/^ +/, "")
                .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        
        // Redirect to home
        navigate('/', { replace: true });
    }, [dispatch, navigate]);

    const resetTimer = useCallback(() => {
        setCountdown(timeout);
    }, [timeout]);

    useEffect(() => {
        // Events that reset the timer
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

        // Reset timer on user activity
        const handleActivity = () => {
            resetTimer();
        };

        // Add event listeners
        events.forEach(event => {
            document.addEventListener(event, handleActivity);
        });

        // Countdown interval
        const interval = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    handleLogout();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        // Cleanup
        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleActivity);
            });
            clearInterval(interval);
        };
    }, [resetTimer, handleLogout]);

    // Only show countdown when 15 seconds or less remaining
    if (countdown > 15) {
        return null;
    }

    return (
        <div className="fixed top-4 right-4 bg-white shadow-lg rounded-lg px-4 py-2 border border-gray-200 z-50">
            <div className="flex items-center space-x-2">
                <svg 
                    className={`w-5 h-5 ${
                        countdown <= 10 
                            ? 'text-red-500 animate-pulse' 
                            : 'text-yellow-500'
                    }`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                >
                    <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
                    />
                </svg>
                <span className={`font-semibold ${
                    countdown <= 10 
                        ? 'text-red-500' 
                        : 'text-yellow-500'
                }`}>
                    Session: {countdown}s
                </span>
            </div>
        </div>
    );
};

export default InactivityTimer;
